import FW from '../fw/fw';
import Logger from '../fw/logger';
import GoogleCastText from './google-cast-text';
import GoogleCastHelpers from './google-cast-helpers';


export default class GoogleCast {

  #rmp;
  #castSessionResumed = false;
  #castDisplayMessage = null;
  #castDisplayMessageStatus = null;
  #castError = false;
  #castModuleSet = false;
  #castContext = null;
  #remotePlayerState = '';
  #remotePlayerController = null;
  #volumeBeforeCasting = -1;
  #mutedBeforeCasting = false;
  #castPlayerStateChangedFn = null;
  #castPlayerIsConnectedChangedFn = null;
  #castVolumeLevelChangedFn = null;
  #castIsPausedChangedFn = null;
  #castSessionInfoFn = null;
  #castCurrentTimeChangedFn = null;
  #castDurationChangedFn = null;
  #castStateChangedFn = null;
  #castIsPlayingBreakChangedFn = null;
  #onEndedReloadMediaOnCastingDeviceFn = null;
  #onPlayingSetupRemotePlayerFn = null;
  #initCastFn = null;
  #currentTimeBeforeAd = -1;

  constructor(rmp) {
    this.#rmp = rmp;
    this.#rmp.rmpGoogleCastText = new GoogleCastText(rmp);
    this.#rmp.rmpGoogleCastHelpers = new GoogleCastHelpers(rmp);
  }

  get remotePlayerState() {
    return this.#remotePlayerState;
  }

  get remotePlayerController() {
    return this.#remotePlayerController;
  }

  #resetCast() {
    Logger.print('google-cast', 'resetCast');
    this.#rmp.googleCastConnected = false;
    this.#rmp.castMedia = null;
    this.#remotePlayerState = '';
    this.#rmp.dom.container.classList.remove('rmp-casting', 'rmp-casting-no-volume-control');
    if (this.#rmp.castSession) {
      this.#rmp.castSession.endSession(true);
    }
  }

  #updateCastDisplayMessage() {
    let message;
    if (this.#rmp.remotePlayer) {
      switch (this.#remotePlayerState) {
        case 'LOADING':
        case 'BUFFERING':
          this.#rmp.rmpUIManagement.showLoadingSpin();
          break;
        case 'LOADED':
        case 'ENDED':
        case 'IDLE':
        case 'PLAYING':
        case 'PAUSED':
          this.#rmp.rmpUIManagement.hideLoadingSpin();
          break;
        default:
          break;
      }
      message = this.#remotePlayerState;
    }
    if (!message) {
      message = 'STANDBY';
    }
    if (!this.#castDisplayMessage) {
      const castDisplayMessageArea = FW.createElement('div', 'rmp-cast-diplay-message-area');
      this.#castDisplayMessage = FW.createElement('div', 'rmp-cast-diplay-message-title');
      this.#castDisplayMessageStatus = FW.createElement('div', 'rmp-cast-diplay-message-status');
      castDisplayMessageArea.appendChild(this.#castDisplayMessage);
      castDisplayMessageArea.appendChild(this.#castDisplayMessageStatus);
      const moduleArea = this.#rmp.dom.container.querySelector('.rmp-module-area');
      if (moduleArea) {
        FW.insertBefore(this.#rmp.dom.container, castDisplayMessageArea, moduleArea);
      } else {
        FW.insertBefore(this.#rmp.dom.container, castDisplayMessageArea, this.#rmp.dom.controlBar);
      }
    }
    Logger.print('google-cast', 'update status message to ' + message);
    let msg = message;
    if (this.#rmp.castSession) {
      msg += ' on ' + this.#rmp.castSession.getCastDevice().friendlyName;
    }
    FW.setText(this.#castDisplayMessage, this.#rmp.rmpGoogleCastHelpers.castTitle);
    FW.setText(this.#castDisplayMessageStatus, msg);
  }

  #onEndedReloadMediaOnCastingDevice() {
    this.#rmp.off('play', this.#onEndedReloadMediaOnCastingDeviceFn);
    this.loadCastMedia();
  }

  #setupCastRemotePlayer() {
    Logger.print('google-cast', 'setup remote player');
    this.#rmp.dom.container.classList.add('rmp-casting');
    this.#rmp.castSession = this.#castContext.getCurrentSession();
    if (!this.#rmp.castSession) {
      Logger.print('google-cast', 'could not getCurrentSession of castContext');
      return;
    }
    if (DEBUG) {
      if (this.#rmp.debugLevel === 2) {
        this.#rmp.castSession.addEventListener(window.cast.framework.CastSession.APPLICATION_STATUS_CHANGED, () => {
          Logger.print('google-cast', 'APPLICATION_STATUS_CHANGED');
        });
        this.#rmp.castSession.addEventListener(window.cast.framework.CastSession.APPLICATION_METADATA_CHANGED, () => {
          Logger.print('google-cast', 'APPLICATION_METADATA_CHANGED');
        });
        this.#rmp.castSession.addEventListener(window.cast.framework.CastSession.ACTIVE_INPUT_STATE_CHANGED, () => {
          Logger.print('google-cast', 'ACTIVE_INPUT_STATE_CHANGED');
        });
        this.#rmp.castSession.addEventListener(window.cast.framework.CastSession.VOLUME_CHANGED, () => {
          Logger.print('google-cast', 'VOLUME_CHANGED');
        });
        this.#rmp.castSession.addEventListener(window.cast.framework.CastSession.MEDIA_SESSION, () => {
          Logger.print('google-cast', 'MEDIA_SESSION');
        });
      }
    }
    this.#rmp.googleCastConnected = true;
    this.#onEndedReloadMediaOnCastingDeviceFn = this.#onEndedReloadMediaOnCastingDevice.bind(this);
    this.#rmp.castSession.addMessageListener('urn:x-cast:com.radiantmediaplayer.cast', (namespace, message) => {
      Logger.print('google-cast', 'custom message received under ' + namespace);
      let messageObject = JSON.parse(message);
      if (messageObject.type === 'STATUS' && messageObject.message === 'MEDIA_FINISHED') {
        Logger.print('google-cast', 'MEDIA_FINISHED');
        this.#remotePlayerState = 'ENDED';
        this.#updateCastDisplayMessage();
        this.#rmp.rmpEventsManagement.pauseCallback();
        if (this.#rmp.hasRelated || this.#rmp.hasPlaylist) {
          this.#rmp.rmpEventsManagement.endedListCallback();
        } else {
          this.#rmp.on('play', this.#onEndedReloadMediaOnCastingDeviceFn);
        }
        this.#rmp.dispatch('ended');
      }
    });
    if (!this.#castSessionResumed) {
      this.loadCastMedia();
    } else {
      this.#rmp.castMedia = this.#rmp.castSession.getMediaSession();
      Logger.print('google-cast', 'getMediaSession is', this.#rmp.castMedia);
      // in case media is unloaded (ENDED) on cast device and browser is refresh
      if (this.#rmp.castMedia) {
        if (typeof this.#rmp.dom.seekBar === 'undefined') {
          this.#rmp.rmpSeekManagement.appendSeekBar();
        }
        this.#rmp.mute = true;
        this.#rmp.play().catch(error => {
          this.#rmp.rmpUtils.warning(1014, error);
        });
      } else {
        this.loadCastMedia();
      }
    }
    this.#castSessionResumed = false;
  }

  #castPlayerStateChanged() {
    if (this.#rmp.remotePlayer && this.#rmp.remotePlayer.playerState) {
      Logger.print('google-cast', 'PLAYER_STATE_CHANGED to ' + this.#rmp.remotePlayer.playerState);
      switch (this.#rmp.remotePlayer.playerState) {
        case 'BUFFERING':
          this.#remotePlayerState = 'BUFFERING';
          this.#rmp.dispatch('castwaiting');
          break;
        case 'PLAYING':
          this.#remotePlayerState = 'PLAYING';
          this.#rmp.dispatch('castplaying');
          break;
        case 'IDLE':
          this.#remotePlayerState = 'IDLE';
          break;
        case 'PAUSED':
          this.#remotePlayerState = 'PAUSED';
          break;
        default:
          break;
      }
      this.#updateCastDisplayMessage();
    }
  }

  #onPlayingSetupRemotePlayer() {
    this.#rmp.off('playing', this.#onPlayingSetupRemotePlayerFn);
    this.#setupCastRemotePlayer();
  }

  #castPlayerIsConnectedChanged() {
    Logger.print('google-cast', 'IS_CONNECTED_CHANGED - isConnected is ' + this.#rmp.remotePlayer.isConnected);
    if (this.#rmp.remotePlayer.isConnected) {
      if (!this.#rmp.rmpPlaybackManagement.playerInitialized && !this.#castSessionResumed) {
        this.#rmp.initiatePlayback();
        this.#onPlayingSetupRemotePlayerFn = this.#onPlayingSetupRemotePlayer.bind(this);
        this.#rmp.on('playing', this.#onPlayingSetupRemotePlayerFn);
      } else {
        this.#setupCastRemotePlayer();
      }
    } else {
      this.#rmp.off('play', this.#onEndedReloadMediaOnCastingDeviceFn);
      if (!this.#castError) {
        if (this.#rmp.remotePlayer) {
          // restore local player
          if (!this.#rmp.remotePlayer.savedPlayerState.isPaused) {
            this.#rmp.play().catch(error => {
              this.#rmp.rmpUtils.warning(1014, error);
            });
          }
          // seek locally to match last know currentTime from remote player
          if (this.#rmp.isVod && this.#rmp.remotePlayer &&
            this.#rmp.remotePlayer.savedPlayerState && this.#rmp.remotePlayer.savedPlayerState.currentTime) {
            let remotePlayerCurrentTime = this.#rmp.remotePlayer.savedPlayerState.currentTime;
            if (FW.isFiniteNumber(remotePlayerCurrentTime) && remotePlayerCurrentTime >= 0) {
              this.#rmp.seekTo(remotePlayerCurrentTime * 1000).catch(error => {
                this.#rmp.rmpUtils.warning(1016, error);
              });
            }
          } else if (this.#rmp.isDvr) {
            // jump to live for DVR
            this.#rmp.syncToLiveEdge().catch(error => {
              this.#rmp.rmpUtils.warning(1019, error);
            });
          }
          // deal with volume
          if (this.#rmp.remotePlayer.canControlVolume && this.#rmp.googleCastAllowSenderVolumeManagement) {
            const isMuted = this.#rmp.remotePlayer.isMuted;
            if (isMuted) {
              this.#rmp.mute = true;
            } else {
              const inputVolume = this.#rmp.remotePlayer.volumeLevel;
              let newVolume;
              if (inputVolume <= 0) {
                newVolume = 0;
              } else if (inputVolume >= 1) {
                newVolume = 1;
              } else {
                newVolume = inputVolume;
              }
              this.#rmp.dom.video.volume = newVolume;
            }
          } else {
            if (this.#mutedBeforeCasting) {
              this.#rmp.mute = true;
            } else if (this.#volumeBeforeCasting >= 0) {
              this.#rmp.volume = this.#volumeBeforeCasting;
            }
          }
          // restore FCC settings button
          if (this.#rmp.dom.captionsOverlayLevelsArea) {
            FW.show(this.#rmp.dom.fccCompliantSettings);
          }
        }
      } else {
        this.#rmp.play().catch(error => {
          this.#rmp.rmpUtils.warning(1014, error);
        });
      }
      this.#resetCast();
      this.#rmp.dispatch('castmediaunloaded');
    }
  }

  #castIsPausedChanged() {
    Logger.print('google-cast', 'IS_PAUSED_CHANGED');
    if (this.#rmp.remotePlayer) {
      if (this.#rmp.remotePlayer.isPaused) {
        this.#rmp.rmpEventsManagement.pauseCallback();
      } else {
        this.#rmp.rmpEventsManagement.playCallback();
      }
    }
  }

  #castCurrentTimeChanged() {
    this.#rmp.rmpEventsManagement.timeupdateCallback();
  }

  #castDurationChanged() {
    Logger.print('google-cast', 'DURATION_CHANGED');
    if (this.#rmp.remotePlayer.duration ||
      (this.#rmp.remotePlayer.mediaInfo && this.#rmp.remotePlayer.mediaInfo.duration)) {
      this.#rmp.rmpEventsManagement.durationchangeCallback();
    }
  }

  #castVolumeLevelChanged(event) {
    if (this.#rmp.remotePlayer) {
      const isMuted = this.#rmp.remotePlayer.isMuted;
      if (isMuted) {
        Logger.print('google-cast', event.type + ' - volume is muted');
        this.#rmp.mute = true;
      } else {
        const inputVolume = this.#rmp.remotePlayer.volumeLevel;
        let newVolume;
        if (inputVolume <= 0) {
          newVolume = 0;
        } else if (inputVolume >= 1) {
          newVolume = 1;
        } else {
          newVolume = inputVolume;
        }
        Logger.print('google-cast', event.type + ' - volume value is ' + newVolume);
        this.#rmp.dom.video.volume = newVolume;
      }
    }
  }

  #castIsPlayingBreakChanged() {
    Logger.print('google-cast', 'IS_PLAYING_BREAK_CHANGED - isPlayingBreak ' + this.#rmp.remotePlayer.isPlayingBreak);
    if (this.#rmp.rmpAdsHelpers) {
      if (this.#rmp.remotePlayer.isPlayingBreak) {
        this.#currentTimeBeforeAd = this.#rmp.currentTime;
        this.#rmp.rmpAdsHelpers.setUIForAds(true);
        this.#rmp.dispatch('adstarted');
      } else {
        this.#rmp.rmpAdsHelpers.setUIForAds(false);
        this.#rmp.dispatch('adcomplete');
        this.#rmp.seekTo(this.#currentTimeBeforeAd).catch(error => {
          this.#rmp.rmpUtils.warning(1016, error);
        });
      }
    }
  }

  #castInteraction(event) {
    if (event) {
      event.stopPropagation();
    }
    this.#rmp.rmpUIManagement.showChrome();
  }

  #setupCastModule() {
    this.#castModuleSet = true;
    this.#rmp.rmpModuleManagement.appendToModuleBar('cast');
    FW.addEvents(['touchend', 'click'], this.#rmp.dom.cast, this.#castInteraction.bind(this));
    this.#rmp.rmpGoogleCastHelpers.filterContentType();
    this.#castPlayerStateChangedFn = this.#castPlayerStateChanged.bind(this);
    this.#castPlayerIsConnectedChangedFn = this.#castPlayerIsConnectedChanged.bind(this);
    this.#castVolumeLevelChangedFn = this.#castVolumeLevelChanged.bind(this);
    this.#castIsPausedChangedFn = this.#castIsPausedChanged.bind(this);
    this.#castIsPlayingBreakChangedFn = this.#castIsPlayingBreakChanged.bind(this);
    if (this.#rmp.isVod) {
      this.#castCurrentTimeChangedFn = this.#castCurrentTimeChanged.bind(this);
      this.#castDurationChangedFn = this.#castDurationChanged.bind(this);
    }

    // create remote player
    const castFramework = window.cast.framework;
    this.#rmp.remotePlayer = new castFramework.RemotePlayer();
    this.#remotePlayerController = new castFramework.RemotePlayerController(this.#rmp.remotePlayer);
    this.#remotePlayerController.addEventListener(
      castFramework.RemotePlayerEventType.IS_CONNECTED_CHANGED,
      this.#castPlayerIsConnectedChangedFn
    );
    if (this.#rmp.isVod) {
      this.#remotePlayerController.addEventListener(
        castFramework.RemotePlayerEventType.CURRENT_TIME_CHANGED,
        this.#castCurrentTimeChangedFn
      );
      this.#remotePlayerController.addEventListener(
        castFramework.RemotePlayerEventType.DURATION_CHANGED,
        this.#castDurationChangedFn
      );
    }
    this.#remotePlayerController.addEventListener(
      castFramework.RemotePlayerEventType.IS_PAUSED_CHANGED,
      this.#castIsPausedChangedFn
    );
    this.#remotePlayerController.addEventListener(
      castFramework.RemotePlayerEventType.VOLUME_LEVEL_CHANGED,
      this.#castVolumeLevelChangedFn
    );
    this.#remotePlayerController.addEventListener(
      castFramework.RemotePlayerEventType.IS_MUTED_CHANGED,
      this.#castVolumeLevelChangedFn
    );
    this.#remotePlayerController.addEventListener(
      castFramework.RemotePlayerEventType.PLAYER_STATE_CHANGED,
      this.#castPlayerStateChangedFn
    );
    this.#remotePlayerController.addEventListener(
      castFramework.RemotePlayerEventType.IS_PLAYING_BREAK_CHANGED,
      this.#castIsPlayingBreakChangedFn
    );
  }

  #castStateChanged(event) {
    if (event) {
      const castFramework = window.cast.framework;
      switch (event.castState) {
        case castFramework.CastState.NO_DEVICES_AVAILABLE:
          Logger.print('google-cast', 'CAST_STATE_CHANGED to NO_DEVICES_AVAILABLE');
          break;
        case castFramework.CastState.NOT_CONNECTED:
          Logger.print('google-cast', 'CAST_STATE_CHANGED to NOT_CONNECTED');
          if (!this.#castModuleSet) {
            this.#setupCastModule();
          }
          break;
        case castFramework.CastState.CONNECTED:
          Logger.print('google-cast', 'CAST_STATE_CHANGED to CONNECTED');
          if (!this.#castModuleSet) {
            this.#setupCastModule();
          }
          break;
        case castFramework.CastState.CONNECTING:
          Logger.print('google-cast', 'CAST_STATE_CHANGED to CONNECTING');
          break;
        default:
          break;
      }
    }
  }

  #castSessionInfo(event) {
    if (event) {
      const castFramework = window.cast.framework;
      switch (event.sessionState) {
        case castFramework.SessionState.NO_SESSION:
          Logger.print('google-cast', 'SESSION_STATE_CHANGED to NO_SESSION');
          break;
        case castFramework.SessionState.SESSION_STARTING:
          Logger.print('google-cast', 'SESSION_STATE_CHANGED to SESSION_STARTING');
          break;
        case castFramework.SessionState.SESSION_STARTED:
          Logger.print('google-cast', 'SESSION_STATE_CHANGED to SESSION_STARTED');
          break;
        case castFramework.SessionState.SESSION_START_FAILED:
          Logger.print('google-cast', 'SESSION_STATE_CHANGED to SESSION_START_FAILED');
          this.#resetCast();
          break;
        case castFramework.SessionState.SESSION_ENDING:
          Logger.print('google-cast', 'SESSION_STATE_CHANGED to SESSION_ENDING');
          break;
        case castFramework.SessionState.SESSION_ENDED:
          Logger.print('google-cast', 'SESSION_STATE_CHANGED to SESSION_ENDED');
          break;
        case castFramework.SessionState.SESSION_RESUMED:
          Logger.print('google-cast', 'SESSION_STATE_CHANGED to SESSION_RESUMED');
          this.#castSessionResumed = true;
          break;
        default:
          break;
      }
    }
  }

  #initCast() {
    Logger.print('google-cast', 'init Google Cast');
    this.#rmp.off('adcontentresumerequested', this.#initCastFn);
    if (typeof window.cast === 'undefined' || typeof window.cast.framework === 'undefined' ||
      typeof window.cast.framework.CastContext === 'undefined') {
      Logger.print('google-cast', 'could not find window.cast - exit');
      return;
    }
    this.#castContext = window.cast.framework.CastContext.getInstance();
    if (!this.#castContext) {
      Logger.print('google-cast', 'could not getInstance of CastContext');
      return;
    }
    let googleCastReceiverAppId = this.#rmp.googleCastReceiverAppId;
    if (googleCastReceiverAppId === 'cast-default') {
      googleCastReceiverAppId = window.chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID;
    }
    Logger.print('google-cast', 'filtered googleCastReceiverAppId is ' + googleCastReceiverAppId);
    this.#castContext.setOptions({
      receiverApplicationId: googleCastReceiverAppId,
      autoJoinPolicy: window.chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
      androidReceiverCompatible: this.#rmp.googleCastAndroidReceiverCompatible
    });
    this.#castStateChangedFn = this.#castStateChanged.bind(this);
    this.#castContext.addEventListener(
      window.cast.framework.CastContextEventType.CAST_STATE_CHANGED, this.#castStateChangedFn);
    this.#castSessionInfoFn = this.#castSessionInfo.bind(this);
    this.#castContext.addEventListener(
      window.cast.framework.CastContextEventType.SESSION_STATE_CHANGED, this.#castSessionInfoFn);
    const currentCastState = this.#castContext.getCastState();
    Logger.print('google-cast', 'currentCastState is ' + currentCastState);
    if (currentCastState !== window.cast.framework.CastState.NO_DEVICES_AVAILABLE) {
      this.#setupCastModule();
    }
    this.#rmp.dispatch('castapiavailable');
  }

  setCastAudioTrack(index) {
    if (this.#rmp.remotePlayer && this.#rmp.remotePlayer.isMediaLoaded &&
      this.#rmp.castMedia &&
      this.#rmp.castMediaTracks &&
      this.#rmp.castMediaTracks.length > 0) {
      let audioTracks = this.#rmp.castMediaTracks.filter(track => {
        return track.type === 'AUDIO';
      });
      if (audioTracks[index] && typeof audioTracks[index].trackId === 'number') {
        let newAudioTrackId = audioTracks[index].trackId;
        Logger.print('google-cast', 'set audio track on receiving device with id ' + newAudioTrackId);
        let tracksInfoRequest = new window.chrome.cast.media.EditTracksInfoRequest([newAudioTrackId]);
        this.#rmp.castMedia.editTracksInfo(tracksInfoRequest, () => { return; }, console.warn);
      }
    }
  }

  isCastMediaLoaded() {
    if (this.#rmp.remotePlayer && this.#rmp.remotePlayer.isMediaLoaded && this.#remotePlayerController &&
      this.#rmp.castMedia) {
      return true;
    }
    return false;
  }

  #updateCastMediaTracks() {
    if (this.#rmp.castMedia && this.#rmp.castMedia.media && Array.isArray(this.#rmp.castMedia.media.tracks) &&
      this.#rmp.castMedia.media.tracks.length > 0) {
      this.#rmp.castMediaTracks = this.#rmp.castMedia.media.tracks;
    } else {
      this.#rmp.castMediaTracks = [];
    }
    Logger.print('google-cast', 'updated cast media tracks follow', this.#rmp.castMediaTracks);
  }

  loadCastMedia(newSession) {
    Logger.print('google-cast', 'load media on cast device requested');
    this.#castError = false;
    let localVolume = this.#rmp.volume;
    let localMuted = this.#rmp.mute;
    let localPlayerCurrentTime = this.#rmp.currentTime;

    const mediaInfo = this.#rmp.rmpGoogleCastHelpers.prepareLoadCastMedia();
    if (this.#rmp.googleCastEnableUITextDisplayer) {
      mediaInfo.customData.enableUITextDisplayer = this.#rmp.googleCastEnableUITextDisplayer;
    }
    if (this.#rmp.hlsJSFetchXhrWithCredentials ||
      (this.#rmp.shakaRequestConfiguration.manifest &&
        this.#rmp.shakaRequestConfiguration.manifest.credentials === true) ||
      (this.#rmp.shakaRequestConfiguration.segment &&
        this.#rmp.shakaRequestConfiguration.segment.credentials === true)) {
      mediaInfo.customData.credentials = true;
    }
    if (!FW.isEmptyObject(this.#rmp.googleCastHeaders)) {
      mediaInfo.customData.headers = this.#rmp.googleCastHeaders;
    }
    if (!FW.isEmptyObject(this.#rmp.googleCastData)) {
      mediaInfo.customData.additionalData = this.#rmp.googleCastData;
    }
    const castMedia = window.chrome.cast.media;
    // CC
    if (this.#rmp.hasExternalCCFiles) {
      let ccCastArray = [];
      this.#rmp.ccFiles.forEach((ccFile, index) => {
        ccCastArray[index] = new castMedia.Track(index + 1, castMedia.TrackType.TEXT);
        ccCastArray[index].trackContentId = ccFile.uri;
        ccCastArray[index].trackContentType = 'text/vtt';
        ccCastArray[index].subtype = castMedia.TextTrackType.SUBTITLES;
        ccCastArray[index].name = ccFile.name;
        ccCastArray[index].language = ccFile.lng;
        ccCastArray[index].customData = null;
      });
      mediaInfo.streamType = castMedia.StreamType.BUFFERED;
      const castTextStyle = this.#rmp.rmpGoogleCastText.setCastTextStyle();
      if (castTextStyle) {
        mediaInfo.textTrackStyle = castTextStyle;
      }
      mediaInfo.duration = null;
      mediaInfo.tracks = ccCastArray;
    }
    this.#remotePlayerState = 'LOADING';
    this.#updateCastDisplayMessage();
    Logger.print('google-cast', 'mediaInfo:', mediaInfo);
    let request = new castMedia.LoadRequest(mediaInfo);
    if (this.#rmp.hasExternalCCFiles &&
      this.#rmp.rmpCCManagement &&
      this.#rmp.rmpCCManagement.defaultTextTrackIndex > 0) {
      request.activeTrackIds = [this.#rmp.rmpCCManagement.defaultTextTrackIndex];
    }
    this.#volumeBeforeCasting = this.#rmp.volume;
    this.#mutedBeforeCasting = this.#rmp.mute;
    this.#rmp.castSession.loadMedia(request).then(() => {
      Logger.print('google-cast', 'successfully loaded media on receiving device');
      this.#rmp.pauseRequestedByLoadCastMedia = false;
      this.#remotePlayerState = 'LOADED';
      this.#updateCastDisplayMessage();
      this.#rmp.rmpEventsManagement.playCallback();

      // since September 4, 2024 November 1, 2024 Web Receiver 3.0.0133 3.0.0135 3.0.0137
      // getMediaSession is only define at castplaying event not castmedialoaded so we need this check
      this.#rmp.castMedia = this.#rmp.castSession.getMediaSession();
      this.#updateCastMediaTracks();
      if (this.#rmp.castMedia === null) {
        this.#rmp.one('castplaying', () => {
          this.#rmp.castMedia = this.#rmp.castSession.getMediaSession();
          this.#updateCastMediaTracks();
        });
      }

      if (newSession) {
        localPlayerCurrentTime = 0;
      }

      if (localPlayerCurrentTime > 0) {
        this.#rmp.seekTo(localPlayerCurrentTime).catch(error => {
          this.#rmp.rmpUtils.warning(1016, error);
        });
      }

      // set captions track in cast receiver if one is active on sender
      if (this.#rmp.dom.captions && this.#rmp.dom.captionsOverlayLevelsArea) {
        let currentCCIndex = 0;
        const rmpCCButtons = this.#rmp.dom.captionsOverlayLevelsArea.querySelectorAll('.rmp-overlay-level');
        if (rmpCCButtons.length > 0) {
          for (let i = 0, len = rmpCCButtons.length; i < len; i++) {
            if (rmpCCButtons[i].classList.contains('rmp-overlay-level-active')) {
              currentCCIndex = i;
              break;
            }
          }
        }
        if (currentCCIndex > 0) {
          this.#rmp.rmpGoogleCastText.setCastTextTrack(currentCCIndex - 1);
        }
        // remove FCC settings menu as we do not set those dynamically for Cast
        FW.showFlex(this.#rmp.dom.captionsOverlayLevelsArea);
        FW.hide(this.#rmp.dom.captionsOverlayFCCSettingsArea);
        // hide FCC settings button
        FW.hide(this.#rmp.dom.fccCompliantSettings);
      }
      // set current audio track in cast receiver if changed in-between
      if (this.#rmp.dom.audio && this.#rmp.dom.audioOverlayLevelsArea) {
        let currentAudioIndex = 0;
        const rmpAudioButtons = this.#rmp.dom.audioOverlayLevelsArea.querySelectorAll('.rmp-overlay-level');
        if (rmpAudioButtons.length > 0) {
          for (let i = 0, len = rmpAudioButtons.length; i < len; i++) {
            if (rmpAudioButtons[i].classList.contains('rmp-overlay-level-active')) {
              currentAudioIndex = i;
              break;
            }
          }
        }
        if (currentAudioIndex > 0) {
          this.setCastAudioTrack(currentAudioIndex);
        }
      }
      if (this.#rmp.remotePlayer.canControlVolume && this.#rmp.googleCastAllowSenderVolumeManagement) {
        Logger.print('google-cast', 'receiver supports controlling volume with sender');
        // adjust volume to match local player
        if (localMuted) {
          if (!this.#rmp.remotePlayer.isMuted) {
            this.#remotePlayerController.muteOrUnmute();
          }
        } else if (localVolume >= 0 && localVolume <= 1) {
          this.#rmp.remotePlayer.volumeLevel = localVolume;
          this.#remotePlayerController.setVolumeLevel();
        }
      } else {
        this.#rmp.dom.container.classList.add('rmp-casting-no-volume-control');
        Logger.print('google-cast', 'receiver does NOT support controlling volume with sender');
      }
      this.#rmp.dispatch('castmedialoaded');
    }).catch(error => {
      this.#resetCast();
      this.#castError = true;
      this.#rmp.dispatch('castmediaerror');
      this.#rmp.rmpUtils.warning(16001, error);
    });
  }

  destroy() {
    if (this.#rmp.googleCastConnected) {
      this.#resetCast();
    }
    if (this.#remotePlayerController) {
      const RemotePlayerEventType = window.cast.framework.RemotePlayerEventType;
      this.#remotePlayerController.removeEventListener(
        RemotePlayerEventType.PLAYER_STATE_CHANGED,
        this.#castPlayerStateChangedFn
      );
      this.#remotePlayerController.removeEventListener(
        RemotePlayerEventType.IS_CONNECTED_CHANGED,
        this.#castPlayerIsConnectedChangedFn
      );
      this.#remotePlayerController.removeEventListener(
        RemotePlayerEventType.VOLUME_LEVEL_CHANGED,
        this.#castVolumeLevelChangedFn
      );
      this.#remotePlayerController.removeEventListener(
        RemotePlayerEventType.IS_PAUSED_CHANGED,
        this.#castIsPausedChangedFn
      );
      this.#remotePlayerController.removeEventListener(
        RemotePlayerEventType.IS_PLAYING_BREAK_CHANGED,
        this.#castIsPlayingBreakChangedFn
      );
      if (this.#rmp.isVod) {
        this.#remotePlayerController.removeEventListener(
          RemotePlayerEventType.CURRENT_TIME_CHANGED,
          this.#castCurrentTimeChangedFn
        );
        this.#remotePlayerController.removeEventListener(
          RemotePlayerEventType.DURATION_CHANGED,
          this.#castDurationChangedFn
        );
      }
    }
    if (this.#castContext) {
      const CastContextEventType = window.cast.framework.CastContextEventType;
      this.#castContext.removeEventListener(
        CastContextEventType.CAST_STATE_CHANGED,
        this.#castStateChangedFn
      );
      this.#castContext.removeEventListener(
        CastContextEventType.SESSION_STATE_CHANGED,
        this.#castSessionInfoFn
      );
    }
    if (this.#rmp.ads) {
      this.#rmp.off('adcontentresumerequested', this.#initCastFn);
    }
  }

  init() {
    //googleCast may have been set to false in main.js
    if (!this.#rmp.googleCast) {
      return;
    }
    Logger.print('google-cast', 'player is ready init Google Cast');
    this.#initCastFn = this.#initCast.bind(this);
    if (!window.chrome.cast || !window.chrome.cast.isAvailable) {
      let castSenderScript = 'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1';
      window.__onGCastApiAvailable = (isAvailable) => {
        if (isAvailable && window.chrome.cast) {
          Logger.print('google-cast', 'API available - at ' + (FW.getNow() - this.#rmp.refTime) + ' ms');
          // in case an ad is on stage when cast framework is loaded we need to defer cast init 
          // when ad has finished to avoid cast icon to appear on linear ad
          if (this.#rmp.linearAdOnStage) {
            this.#rmp.on('adcontentresumerequested', this.#initCastFn);
          } else {
            this.#initCastFn();
          }
          delete window.__onGCastApiAvailable;
        }
      };
      Logger.print('google-cast', 'cast_sender.js starts loading at ' + (FW.getNow() - this.#rmp.refTime) + ' ms');
      FW.getScript(
        castSenderScript,
        () => { return; },
        this.#rmp.rmpUtils.warning.bind(this.#rmp.rmpUtils, 16000),
        this.#rmp.shadowRoot
      );
    } else {
      this.#initCastFn();
    }
  }
}
